import { useBaseStore } from "./stores/base";
import { ApiError, Result } from "./api/base";

export interface Toaster {
	translationKey: string;
	translatedMessage?: string;
	state: "warning" | "error" | "success" | "primary";
	position?: "top" | "bottom";
	offset?: string;
	userMustClose?: boolean;
	hideDelay?: number | undefined;
}

const defaultToasterPosition: {
	position: Toaster["position"];
	offset: Toaster["offset"];
} = {
	position: "bottom",
	offset: "0px",
};
export interface ToasterConfig {
	successTranslationKey?: string;
	loadingTranslationKey?: string;
	errorTranslationKey?: string;
	hideDelayAfterErrorInMs?: number;
	hideDelayAfterSuccessInMs?: number;
	position?: Toaster["position"];
	offset?: Toaster["offset"];
	userMustClose?: boolean;
}

const defaultToasterConfig: ToasterConfig = {
	successTranslationKey: "toaster.success",
	loadingTranslationKey: "toaster.loading",
	errorTranslationKey: "toaster.error",
	hideDelayAfterErrorInMs: 10000,
	hideDelayAfterSuccessInMs: 4000,
	userMustClose: false,
	...defaultToasterPosition,
};

export function toastApiResult<T, E>(
	promise: Promise<Result<T, ApiError>>,
	config?: ToasterConfig
) {
	const toasterConfig = { ...defaultToasterConfig, ...config };

	showToaster({
		state: "warning",
		translatedMessage: "Loading",
		translationKey: toasterConfig.loadingTranslationKey!,
		position: toasterConfig.position,
		hideDelay: toasterConfig.hideDelayAfterSuccessInMs,
	});

	promise.then((r) => {
		if (r.isOk()) {
			hideToaster({
				state: "success",
				translatedMessage: "Successfully executed action",
				translationKey: toasterConfig.successTranslationKey!,
				position: toasterConfig.position,
				offset: toasterConfig.offset,
				hideDelay: toasterConfig.hideDelayAfterSuccessInMs,
			});
		} else {
			let translatedMessage = "";

			const errorBag = r.error.errors
				? r.error.errors.values().next().value
				: null;

			if (r.error.message || (errorBag && errorBag.length > 0)) {
				translatedMessage = errorBag ? errorBag[0] : r.error.message ?? "";
			}

			hideToaster({
				state: "error",
				translationKey: toasterConfig.errorTranslationKey!,
				translatedMessage,
				position: toasterConfig.position,
				offset: toasterConfig.offset,
				hideDelay: toasterConfig.hideDelayAfterErrorInMs,
			});
		}

		return r;
	});

	return promise;
}

export function useToasterForNotification(
	msg: string,
	timeDisplayed: number = 1500,
	state: "warning" | "error" | "success" | "primary" = "primary"
) {
	showToaster({
		state,
		translatedMessage: "",
		translationKey: "",
		position: "bottom",
		hideDelay: 0,
	});

	hideToaster({
		state,
		translatedMessage: msg,
		translationKey: "",
		position: "bottom",
		hideDelay: timeDisplayed,
	});
}

export function showToaster(toaster: Toaster) {
	toaster = { ...defaultToasterPosition, ...toaster };
	const store = useBaseStore();
	store.toaster = toaster;
}

export function hideToaster(toaster: Toaster) {
	if (toaster) {
		showToaster(toaster);
	}

	setTimeout((h) => {
		const store = useBaseStore();
		store.toaster = null;
	}, toaster.hideDelay);
}
